import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { redeemSessionScan } from '../features/sessionScans';
import {
  buildExactSessionDestination,
  clearSessionScanContinuation,
  parseAuthReturnDestination,
  storeSessionScanContinuation,
} from '../lib/sessionScanDestination';
import { useAuthStore } from '../store/authStore';

const INVALID_LINK_MESSAGE =
  'This session link is invalid or no longer available. Ask for a new scan link.';
const ACCOUNT_DISABLED_MESSAGE = 'Restore account access before opening this saved session link.';

export function SessionScanPage() {
  const navigate = useNavigate();
  const { locator = '' } = useParams();
  const token = useAuthStore((state) => state.token);
  const accountDisabled = useAuthStore((state) => state.user?.accountDisabled === true);
  const requestStarted = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const scanPath = `/s/v1/${locator}`;
  const validScanPath = parseAuthReturnDestination(scanPath)?.startsWith('/s/v1/') === true;

  const redeem = useCallback(async () => {
    if (!token || accountDisabled || !validScanPath || requestStarted.current) {
      return;
    }

    requestStarted.current = true;
    setError(null);
    try {
      const result = await redeemSessionScan(locator);
      const destination = buildExactSessionDestination(result.sessionId);
      if (!destination) {
        throw new Error('Invalid exact session destination');
      }

      clearSessionScanContinuation(window.sessionStorage);
      navigate(destination, { replace: true });
    } catch {
      setError(INVALID_LINK_MESSAGE);
      requestStarted.current = false;
    }
  }, [accountDisabled, locator, navigate, token, validScanPath]);

  useEffect(() => {
    if (!validScanPath) {
      setError(INVALID_LINK_MESSAGE);
      return;
    }

    storeSessionScanContinuation(scanPath, window.sessionStorage);

    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    if (accountDisabled) {
      setError(ACCOUNT_DISABLED_MESSAGE);
      return;
    }

    void redeem();
  }, [accountDisabled, navigate, redeem, scanPath, token, validScanPath]);

  function abandonScan() {
    clearSessionScanContinuation(window.sessionStorage);
    navigate('/sessions', { replace: true });
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', px: 2 }}>
      <Stack spacing={2} sx={{ width: '100%', maxWidth: 480 }} alignItems="center">
        <Typography variant="h4" component="h1" fontWeight={800} textAlign="center">
          Open race session
        </Typography>
        {error ? (
          <>
            <Alert severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {accountDisabled ? (
                <Button variant="contained" onClick={() => navigate('/drivers?billing=1')}>
                  Restore account access
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => void redeem()}
                  disabled={!token || !validScanPath}
                >
                  Retry link
                </Button>
              )}
              <Button variant="outlined" onClick={abandonScan}>
                Return to Sessions
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <CircularProgress aria-label="Opening session" />
            <Typography color="text.secondary">Opening your session in Paddiq…</Typography>
          </>
        )}
      </Stack>
    </Box>
  );
}
